import './App.css'
import { Suspense } from 'react'
import { Toaster } from "@/components/ui/toaster"
import ErrorBoundary from "@/components/ErrorBoundary"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import VisualEditAgent from '@/lib/VisualEditAgent'
import NavigationTracker from '@/lib/NavigationTracker'
import { pagesConfig } from './pages.config'
import ModuleGuard from '@/components/ModuleGuard'
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import Login from './pages/Login';
import Registro from './pages/Registro';
import EsqueciSenha from './pages/EsqueciSenha';
import RedefinirSenha from './pages/RedefinirSenha';
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminEmpresas from './pages/admin/AdminEmpresas';
import AdminEmpresaDetalhe from './pages/admin/AdminEmpresaDetalhe';
import AdminPlanos from './pages/admin/AdminPlanos';
import AdminConfigAsaas from './pages/admin/AdminConfigAsaas';
import AdminConfigEditalAI from './pages/admin/AdminConfigEditalAI';
import AdminConfigSmtp from './pages/admin/AdminConfigSmtp';
import AdminConfigGDrive from './pages/admin/AdminConfigGDrive';
import AdminCertifyGDriveLink from './pages/admin/AdminCertifyGDriveLink';
import AdminConfigContratante from './pages/admin/AdminConfigContratante';
import AdminErrorMonitoring from './pages/admin/AdminErrorMonitoring';
import AdminRecuperar from './pages/admin/AdminRecuperar';
import AdminGuidedTour from './pages/admin/AdminGuidedTour';
import AdminSuporte from './pages/admin/AdminSuporte';
import AssinaConvite from './pages/AssinaConvite';
import AssinaValidar from './pages/AssinaValidar';
import CertifyVerify from './pages/CertifyVerify';

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;

/** Fallback ao carregar chunks lazy (ex.: Consultar Editais). Componentes síncronos renderizam sem atraso. */
function PageRouteSuspenseFallback() {
  return (
    <div className="flex min-h-[40vh] w-full flex-col items-center justify-center gap-3 p-8 text-muted-foreground">
      <div
        className="h-9 w-9 rounded-full border-2 border-muted border-t-primary animate-spin"
        aria-hidden
      />
      <span className="text-sm">Carregando módulo…</span>
    </div>
  );
}

const LayoutWrapper = ({ children, currentPageName }) => Layout ?
  <Layout currentPageName={currentPageName}>{children}</Layout>
  : <>{children}</>;

const AuthenticatedApp = () => {
  const location = useLocation();
  const { isLoadingAuth, isLoadingPublicSettings, authError, isAuthenticated, navigateToLogin, empresa } = useAuth();

  if (location.pathname.includes('//')) {
    const normalized = location.pathname.replace(/\/+/g, '/') + location.search;
    return <Navigate to={normalized} replace />;
  }

  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    }
    if (authError.type === 'auth_required') {
      navigateToLogin();
      return null;
    }
  }

  if (isAuthenticated && empresa?.status === 'awaiting_payment') {
    const pathLower = location.pathname.toLowerCase();
    if (!pathLower.startsWith('/assinaturaplano')) {
      return <Navigate to={`/assinaturaplano${location.search || ''}`} replace />;
    }
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/Login" element={<Login />} />
        <Route path="/Registro" element={<Registro />} />
        {/* Links errados ou partilhados: plano/pré-pagamento pertence ao cadastro, não ao checkout. */}
        <Route
          path="/assinaturaplano"
          element={<Navigate to={`/Registro${location.search || ''}`} replace />}
        />
        <Route
          path="/AssinaturaPlano"
          element={<Navigate to={`/Registro${location.search || ''}`} replace />}
        />
        <Route path="/EsqueciSenha" element={<EsqueciSenha />} />
        <Route path="/RedefinirSenha" element={<RedefinirSenha />} />
        <Route path="/assina/convite/:token" element={<AssinaConvite />} />
        <Route path="/assina/validar" element={<AssinaValidar />} />
        <Route path="/verify/:id" element={<CertifyVerify />} />
        <Route path="*" element={<Navigate to="/Login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={
        <LayoutWrapper currentPageName={mainPageKey}>
          <Suspense fallback={<PageRouteSuspenseFallback />}>
            <MainPage />
          </Suspense>
        </LayoutWrapper>
      } />
      <Route path="/Login" element={<Navigate to="/" replace />} />
      <Route path="/Registro" element={<Navigate to="/" replace />} />
      <Route path="/EsqueciSenha" element={<EsqueciSenha />} />
      <Route path="/RedefinirSenha" element={<RedefinirSenha />} />
      <Route path="/assina/convite/:token" element={<AssinaConvite />} />
      <Route path="/assina/validar" element={<AssinaValidar />} />
      <Route path="/verify/:id" element={<CertifyVerify />} />
      {Object.entries(Pages).map(([path, Page]) => (
        <Route
          key={path}
          path={`/${path.toLowerCase().replace(/ /g, '-')}`}
          element={
            <ModuleGuard moduleId={path}>
              <LayoutWrapper currentPageName={path}>
                <Suspense fallback={<PageRouteSuspenseFallback />}>
                  <Page />
                </Suspense>
              </LayoutWrapper>
            </ModuleGuard>
          }
        />
      ))}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};


const AdminRoutes = () => (
  <Routes>
    <Route path="/admin" element={<AdminLogin />} />
    <Route path="/admin/*" element={<AdminLayout />}>
      <Route path="dashboard" element={<AdminDashboard />} />
      <Route path="empresas" element={<AdminEmpresas />} />
      <Route path="empresas/:id" element={<AdminEmpresaDetalhe />} />
      <Route path="planos" element={<AdminPlanos />} />
      <Route path="config/asaas" element={<AdminConfigAsaas />} />
      <Route path="config/edital-ai" element={<AdminConfigEditalAI />} />
      <Route path="config/smtp" element={<AdminConfigSmtp />} />
      <Route path="config/gdrive" element={<AdminConfigGDrive />} />
      <Route path="config/certify-gdrive-link" element={<AdminCertifyGDriveLink />} />
      <Route path="config/contratante" element={<AdminConfigContratante />} />
      <Route path="config/guided-tour" element={<AdminGuidedTour />} />
      <Route path="monitoramento-erros" element={<AdminErrorMonitoring />} />
      <Route path="suporte" element={<AdminSuporte />} />
      <Route path="recuperar" element={<AdminRecuperar />} />
      <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
    </Route>
  </Routes>
);

function AppContent() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  if (isAdmin) {
    return <AdminRoutes />;
  }
  return <AuthenticatedApp />;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <QueryClientProvider client={queryClientInstance}>
          <Router>
            <NavigationTracker />
            <Routes>
              <Route path="*" element={<AppContent />} />
            </Routes>
          </Router>
          <Toaster />
          <VisualEditAgent />
        </QueryClientProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
